export enum ObjectiveName {
  printResearchNotes = "Print Research Notes",
  fixBroadcast = "Fix Broadcast",
  collectSamples = "Collect Samples",
  cctvFootage = "CCTV Footage",
  snowTruck = "Snow Truck",
  helicopter = "Helicopter",
  panicRoom = "Panic Room",
}

export enum Location {
  water = "Water",
  storage = "Storage",
  maintenance = "Maintenance",
  warehouse = "Warehouse",
  research = "Research",
  fireAndMedical = "Fire & Medical",
  communications = "Communications",
  dormsA = "Dorms A",
  dormsB = "Dorms B",
  canteen = "Canteen",
  garage = "Garage",
  recreational = "Recreational",
  server = "Server",
}

export enum Item {
  maintenanceAccessCard = "Maintenance Access Card",
  labsAccessCard = "Labs Access Card",
  securityAccessCard = "Security Access Card",
  serverAccessCard = "Server Access Card",
  snowTruckKeys = "Snow Truck Keys",
  fuse = "Fuse",
  snowTruckHeadlight = "Snow Truck Headlight",
  fuelCanister = "Fuel Canister",
  helicopterKeys = "Helicopter Keys",
  deicer = "De-icer",
  boltCutters = "Bolt Cutters",
  blowtorch = "Blowtorch",
  usbHardwareKey = "USB Hardware Key",
}

export enum Task {
  unlockSecurityFromServer = "Unlock Security from the Server Room",
  collectSamples = "Collect Samples",
  printResearchNotes = "Print Research Notes",
  refuelTheTruck = "Refuel the truck",
  refuelTheHelicopterOne = "Refuel the helicopter (1)",
  refuelTheHelicopterTwo = "Refuel the helicopter (2)",
  unlockMaintenance = "Unlock Maintenance",
  unlockLabs = "Unlock Labs",
  unlockSecurity = "Unlock Security using Security Access Card",
  unlockUpperServer = "Unlock Upper Server",
  unlockLowerServer = "Unlock Lower Server",
  fixBroadcast = "Fix Broadcast",
  unlockTheTruckWithKeys = "Unlock the truck with keys",
  unlockTheHelicopterWithKeys = "Unlock the helicopter with keys",
  cutTheChains = "Cut the chains",
  repairTheHeadlights = "Repair the headlights",
  putTheFuseInToOpenTheGate = "Put the fuse in to open the gate",
  cctvFootage = "Retrieve CCTV Footage",
  unlockPanicRoom = "Unlock Panic Room",
  useDeicer = "Use de-icer",
}

export const locations = [
  Location.research,
  Location.fireAndMedical,
  Location.communications,
  Location.recreational,
  Location.dormsA,
  Location.dormsB,
  Location.canteen,
  Location.server,
  Location.maintenance,
  Location.warehouse,
  Location.garage,
  Location.storage,
  Location.water,
];

export const tasks = [
  Task.unlockSecurityFromServer,
  Task.collectSamples,
  Task.printResearchNotes,
  Task.refuelTheTruck,
  Task.refuelTheHelicopterOne,
  Task.refuelTheHelicopterTwo,
  Task.unlockMaintenance,
  Task.unlockLabs,
  Task.unlockSecurity,
  Task.unlockUpperServer,
  Task.unlockLowerServer,
  Task.fixBroadcast,
  Task.unlockTheTruckWithKeys,
  Task.unlockTheHelicopterWithKeys,
  Task.cutTheChains,
  Task.repairTheHeadlights,
  Task.putTheFuseInToOpenTheGate,
  Task.cctvFootage,
  Task.unlockPanicRoom,
];

export const items = [
  Item.maintenanceAccessCard,
  Item.labsAccessCard,
  Item.securityAccessCard,
  Item.serverAccessCard,
  Item.snowTruckKeys,
  Item.fuse,
  Item.snowTruckHeadlight,
  Item.fuelCanister,
  Item.helicopterKeys,
  Item.deicer,
  Item.boltCutters,
  Item.blowtorch,
  Item.usbHardwareKey,
];

export type Objective = {
  name: ObjectiveName;
  tasks: (Task | Item)[][][];
  requirements: (Task | Item)[];
};

export const snowTruck: Objective = {
  name: ObjectiveName.snowTruck,
  tasks: [
    [[Item.snowTruckKeys], [Task.unlockTheTruckWithKeys]],
    [[Item.fuelCanister], [Task.refuelTheTruck]],
    [[Item.fuse], [Task.putTheFuseInToOpenTheGate]],
    [[Item.snowTruckHeadlight], [Task.repairTheHeadlights]],
  ],
  requirements: [
    Task.unlockTheTruckWithKeys,
    Task.refuelTheTruck,
    Task.putTheFuseInToOpenTheGate,
    Task.repairTheHeadlights,
  ],
};

export const helicopter: Objective = {
  name: ObjectiveName.helicopter,
  tasks: [
    [[Item.helicopterKeys], [Task.unlockTheHelicopterWithKeys]],
    [[Item.fuelCanister], [Task.refuelTheHelicopterOne], [Task.refuelTheHelicopterTwo]],
    [[Item.blowtorch, Item.boltCutters], [Task.cutTheChains]],
    [[Item.deicer], [Task.useDeicer]],
  ],
  requirements: [Task.unlockTheHelicopterWithKeys, Task.refuelTheHelicopterTwo, Task.cutTheChains, Task.useDeicer],
};

export const panicRoom: Objective = {
  name: ObjectiveName.panicRoom,
  tasks: [
    [[Item.serverAccessCard], [Task.unlockLowerServer], [Task.unlockSecurityFromServer]],
    [[Item.serverAccessCard], [Task.unlockUpperServer], [Task.unlockSecurityFromServer]],
    [[Item.securityAccessCard], [Task.unlockSecurity]],
    [[Item.usbHardwareKey], [Task.unlockPanicRoom]],
  ],
  requirements: [Task.unlockPanicRoom],
};

export const printResearchNotes: Objective = {
  name: ObjectiveName.printResearchNotes,
  tasks: [[[Task.printResearchNotes]]],
  requirements: [Task.printResearchNotes],
};

export const fixBroadcast: Objective = {
  name: ObjectiveName.fixBroadcast,
  tasks: [[[Task.fixBroadcast]]],
  requirements: [Task.fixBroadcast],
};

export const collectSamples: Objective = {
  name: ObjectiveName.collectSamples,
  tasks: [[[Item.labsAccessCard], [Task.unlockLabs], [Task.collectSamples]]],
  requirements: [Task.collectSamples],
};

export const cctvFootage: Objective = {
  name: ObjectiveName.cctvFootage,
  tasks: [
    [[Item.serverAccessCard], [Task.unlockLowerServer, Task.unlockUpperServer], [Task.unlockSecurityFromServer]],
    [[Item.securityAccessCard], [Task.unlockSecurity], [Task.cctvFootage]],
  ],
  requirements: [Task.cctvFootage],
};

export const objectives = [
  printResearchNotes,
  fixBroadcast,
  collectSamples,
  cctvFootage,
  snowTruck,
  helicopter,
  panicRoom,
];

export const initialState = Object.fromEntries([...items, ...tasks, ...locations].map((item) => [item, false]));

export const defaultObjectives = Object.fromEntries(
  objectives.map((objective) => [
    objective.name,
    [ObjectiveName.snowTruck, ObjectiveName.helicopter, ObjectiveName.panicRoom].includes(objective.name),
  ])
);

export const groupedItems: { name: string; items: Item[] }[] = [
  {
    name: "Access Cards",
    items: [Item.serverAccessCard, Item.securityAccessCard, Item.labsAccessCard, Item.maintenanceAccessCard],
  },
  { name: "Snow Truck", items: [Item.snowTruckKeys, Item.fuse, Item.snowTruckHeadlight] },
  {
    name: "Helicopter",
    items: [Item.helicopterKeys, Item.deicer, Item.boltCutters, Item.blowtorch],
  },
  { name: "Miscellaneous", items: [Item.usbHardwareKey, Item.fuelCanister] },
];
